import React, { useCallback, useEffect, useState } from 'react';
import { LegalModal, type LegalDocumentType } from './LegalModal';
import styles from './CookieNotice.module.css';

/**
 * Cookie notice (GDPR / ePrivacy).
 *
 * Policyora sets no advertising, analytics or third-party tracking cookies:
 * the only browser storage is the sign-in session, the security/anti-abuse
 * check and the person's own interface preferences. Storage that is strictly
 * necessary for a service the user asked for does not need prior consent, so
 * this is a **notice with an acknowledgement**, not a consent gate — there is
 * nothing to refuse, and a banner offering "reject" for cookies we cannot drop
 * would be misleading.
 *
 * The acknowledgement lives in this browser (localStorage). If storage is
 * blocked the notice simply shows again; it never blocks the page.
 */
export const COOKIE_NOTICE_KEY = 'policyora_cookie_notice_v1';

const read = (): string | null => {
  try {
    return window.localStorage.getItem(COOKIE_NOTICE_KEY);
  } catch {
    return null;   // private window / storage blocked
  }
};

export const CookieNotice: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);
  const [legalDoc, setLegalDoc] = useState<LegalDocumentType>('privacy');
  const barRef = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!read()) setVisible(true);
  }, []);

  // The bar is fixed to the bottom, so without this it sits on top of whatever
  // the page puts there — it covered the "Continue" button on the risk
  // workflow (found 22 Sep 2026). Reserve its height while it is shown.
  useEffect(() => {
    if (!visible) return undefined;
    // Set and clear outright rather than restoring a captured value: the effect
    // can run twice (StrictMode), and the second run would capture the padding
    // this component had just set and "restore" it forever.
    const height = barRef.current?.offsetHeight || 0;
    document.body.style.paddingBottom = `${height + 32}px`;
    return () => { document.body.style.removeProperty('padding-bottom'); };
  }, [visible]);

  const acknowledge = useCallback(() => {
    try {
      window.localStorage.setItem(COOKIE_NOTICE_KEY, new Date().toISOString());
    } catch {
      /* acknowledged for this visit only */
    }
    document.body.style.removeProperty('padding-bottom');
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <>
      <div ref={barRef} className={styles.bar} role="region" aria-label="Cookie notice" data-testid="cookie-notice">
        <p className={styles.text}>
          We use only the cookies this service needs to work — keeping you signed in, keeping the
          site secure, and remembering your interface preferences. No advertising or third-party
          tracking cookies are set.{' '}
          <button
            type="button"
            className={styles.link}
            onClick={() => { setLegalDoc('privacy'); setLegalOpen(true); }}
          >
            Read the privacy policy
          </button>
        </p>
        <button type="button" className={styles.button} onClick={acknowledge}>
          Got it
        </button>
      </div>
      <LegalModal
        open={legalOpen}
        documentType={legalDoc}
        onSelectDocument={setLegalDoc}
        onClose={() => setLegalOpen(false)}
      />
    </>
  );
};

export default CookieNotice;
